import * as React from 'react';
import { useSelector } from 'react-redux';

// Utils
import { t } from '@toolkit/util/i18n';
import { selectors } from '@src/shared/src';
import { isNilOrEmpty } from '@src/shared/src/util/general';
import useBreakpoints from '@src/toolkit/util/useBreakpoints';
// Constants
import { ENVIRONMENT, STATUS, VEHICLE_TYPES } from '@src/shared/src/const/app';
// Actions
// Models
import { BookingItemModel } from '@src/shared/src/models';
// Interfaces
import { IRootState } from '@src/store';
// Components
import {
  BookingInfo,
  CheckoutFooterConfirm,
  CheckoutHeader,
} from '@pod/checkout/components';
import { LoaderOverlay, WarningBanner } from '@toolkit/ui';
import LoaderConfirm from '@toolkit/ui/LoaderConfirm';
import CheckoutSummary from '../components/CheckoutSummary';
import CheckoutConfirmBookedItems from '../components/CheckoutConfirmBookedItems';
import { useAwaitingFlexLaneContract } from '../components/FlexLaneContractWaitProvider';

// Styles

declare const window: any;

type Props = {
  env: ENVIRONMENT;
  bookingItems: BookingItemModel[];
  basketStatus: STATUS;
  totalPrice: number;
};

const CheckoutConfirmConn: React.FC<Props> = ({
  env,
  bookingItems,
  basketStatus,
  totalPrice,
}) => {
  const { isMd } = useBreakpoints();
  const costCenter = useSelector((state: IRootState) =>
    selectors.checkout.costCenter(state.checkout),
  );
  const costUnit = useSelector((state: IRootState) => selectors.checkout.costUnit(state.checkout));
  const referenceCode = useSelector((state: IRootState) =>
    selectors.checkout.referenceCode(state.checkout),
  );
  const remarks = useSelector((state: IRootState) => selectors.checkout.remarks(state.checkout));
  const basketTripsVehicles = useSelector((state: IRootState) =>
    selectors.checkout.basketTripsVehicles(state.checkout),
  );
  const guestPassengers = useSelector((state: IRootState) =>
    selectors.search.guestPassengers(state.search),
  );
  const passengers = useSelector((state: IRootState) =>
    selectors.search.searchPassengers(state.search),
  );
  const bookedBy = useSelector((state: IRootState) => state.checkout.basket.createdBy);
  const flightNumber = useSelector(
    (state: IRootState) => state.checkout.travelBooking?.flightNumber,
  );
  const totalCo2InKg = useSelector(
    (state: IRootState) => state.checkout.currentBooking?.totalCo2InKg,
  );
  const awaitingFlexLaneContract = useAwaitingFlexLaneContract();

  React.useEffect(() => {
    if ((window as any).__IS_WEBVIEW) {
      window.isMobileWebView.postMessage('checkout');
    }
  }, []);

  const renderGuestCheckinWarningBanner = () => {
    if (!isNilOrEmpty(guestPassengers) && basketTripsVehicles.includes(VEHICLE_TYPES.PLANE)) {
      return (
        <WarningBanner type="info" label={t('checkoutConfirmConn.noGuestCheckin.info.message')} />
      );
    }
    return null;
  };

  const renderObbDownloadTicketWarning = () => {
    if (bookingItems.some((item) => item.aggregator?.ident === 'trainline_obb')) {
      return (
        <WarningBanner
          type="warning"
          label={t('checkoutConfirmConn.obbDownloadTicket.info.message')}
        />
      );
    }
  };

  // The FlexLane wait deliberately holds the SAME loader the booking itself is confirmed behind, rather
  // than a second widget of its own: to the customer there is one booking being completed, not two things
  // being waited on (lanes-planes/backend#15086).
  if (
    (basketStatus !== STATUS.CONFIRM_FAILED &&
      basketStatus !== STATUS.CONFIRM_PENDING &&
      basketStatus !== STATUS.CONFIRM_FINISHED) ||
    awaitingFlexLaneContract
  ) {
    return <LoaderOverlay showSpinner={false} showInBox={true} body={<LoaderConfirm />} />;
  }

  return (
    <React.Fragment>
      {!isMd && <CheckoutHeader>{t('checkoutConfirmConn.title.confirmation')}</CheckoutHeader>}
      <CheckoutSummary passengers={passengers} bookedBy={bookedBy} />
      {renderGuestCheckinWarningBanner()}
      {renderObbDownloadTicketWarning()}
      <CheckoutConfirmBookedItems />
      <BookingInfo env={env} bookingItems={bookingItems} showTransportTitle={true} />
      <CheckoutFooterConfirm
          remarks={remarks}
          costCenter={costCenter}
          costUnit={costUnit}
          flightNumber={flightNumber}
          totalPrice={totalPrice}
          referenceCode={referenceCode}
          co2InKg={totalCo2InKg}
        />
    </React.Fragment>
  );
};

export default CheckoutConfirmConn;
